/**
 * 出撃エフェクトの描画モジュール
 *
 * 機体選択画面からゲーム開始への遷移アニメーションを担当します。
 * - Phase 1: UIフェードアウト + 機体移動
 * - Phase 2: エネルギーチャージ
 * - Phase 3: 出撃（ブースター炎 + スピードライン）
 *
 * @module ui/screens/animations/ship-launch-effects
 */

import { getP5 } from '../../../core/p5-instance';
import type { Particle } from '../../../types/game-types';
import { drawBoosterFlame, drawSpeedLines, drawChargeRings, drawShipEngineFlames } from '../../../features/particles/booster-effects';

// ============================================
// 定数
// ============================================
export const LAUNCH_PHASES = {
    PHASE1_END: 30,
    PHASE2_END: 60,
    TOTAL_DURATION: 120,
} as const;

// ============================================
// Phase 1: UIフェードアウト + 機体移動
// ============================================

export interface Phase1Params {
    fadeProgress: number;
    startX: number;
    startY: number;
    centerX: number;
    centerY: number;
    drawShip: (size: number) => void;
    drawFadingCards: (alpha: number) => void;
}

/**
 * Phase 1 の描画: UIフェードアウトと機体移動
 */
export function drawPhase1(params: Phase1Params): void {
    const p = getP5();
    const { fadeProgress, startX, startY, centerX, centerY, drawShip, drawFadingCards } = params;
    const uiAlpha = 255 * (1 - fadeProgress);

    // タイトル（フェードアウト）
    p.fill(0, 255, 255, uiAlpha);
    p.textSize(20);
    p.textAlign(p.CENTER, p.CENTER);
    p.text('SELECT SHIP', p.width / 2, 42);

    // 非選択機体のカード（フェードアウト）
    drawFadingCards(uiAlpha);

    // 選択機体の位置を補間
    const shipX = p.lerp(startX, centerX, easeInOutCubic(fadeProgress));
    const shipY = p.lerp(startY, centerY, easeInOutCubic(fadeProgress));
    const shipScale = p.lerp(1.0, 1.3, fadeProgress);

    p.push();
    p.translate(shipX, shipY);
    p.scale(shipScale);
    drawShip(45);
    p.pop();
}

// ============================================
// Phase 2: エネルギーチャージ
// ============================================

export interface Phase2Params {
    chargeProgress: number;
    centerX: number;
    centerY: number;
    timer: number;
    color: readonly number[];
    particles: Particle[];
    drawShip: (size: number) => void;
}

/**
 * Phase 2 のパーティクル更新: 収束パーティクル生成
 */
export function updatePhase2Particles(
    particles: Particle[],
    centerX: number,
    centerY: number,
    color: readonly number[],
    timer: number
): void {
    const p = getP5();

    // 3フレームごとにパーティクル追加
    if (timer % 3 === 0) {
        const angle = p.random(p.TWO_PI);
        const dist = p.random(100, 150);
        particles.push({
            x: centerX + p.cos(angle) * dist,
            y: centerY + p.sin(angle) * dist,
            vx: 0,
            vy: 0,
            color: p.color(color[0], color[1], color[2]),
            targetX: centerX,
            targetY: centerY,
            size: p.random(3, 8),
            life: 20
        });
    }

    // パーティクル更新
    for (let i = particles.length - 1; i >= 0; i--) {
        const particle = particles[i];
        if (particle.targetX !== undefined && particle.targetY !== undefined) {
            particle.x = p.lerp(particle.x, particle.targetX, 0.15);
            particle.y = p.lerp(particle.y, particle.targetY, 0.15);
        }
        particle.life--;

        if (particle.life <= 0) {
            particles.splice(i, 1);
        }
    }
}

/**
 * Phase 2 の描画: エネルギーチャージ
 */
export function drawPhase2(params: Phase2Params): void {
    const p = getP5();
    const { chargeProgress, centerX, centerY, timer, color, particles, drawShip } = params;

    // エネルギーチャージリング
    drawChargeRings(centerX, centerY, chargeProgress, color, timer);

    // パーティクル描画
    for (const particle of particles) {
        p.fill(color[0], color[1], color[2], 200);
        p.noStroke();
        p.circle(particle.x, particle.y, particle.size);
    }

    // 機体（脈動）
    const pulseScale = 1.3 + p.sin(timer * 0.4) * 0.1;
    p.push();
    p.translate(centerX, centerY);
    p.scale(pulseScale);
    drawShip(45);
    p.pop();

    // 「READY」テキスト
    if (chargeProgress > 0.5) {
        const textAlpha = (chargeProgress - 0.5) * 2 * 255;
        p.fill(255, 255, 255, textAlpha);
        p.textSize(24);
        p.textAlign(p.CENTER, p.CENTER);
        p.text('READY', centerX, centerY - 80);
    }
}

// ============================================
// Phase 3: 出撃
// ============================================

export interface Phase3Params {
    launchProgress: number;
    centerX: number;
    centerY: number;
    color: readonly number[];
    shipType: string;
    drawShip: (size: number) => void;
}

/**
 * Phase 3 の描画: 出撃
 */
export function drawPhase3(params: Phase3Params): void {
    const p = getP5();
    const { launchProgress, centerX, centerY, color, shipType, drawShip } = params;

    // 背景にスピードライン
    drawSpeedLines(launchProgress);

    // 「LAUNCH!」テキスト
    if (launchProgress < 0.5) {
        const textScale = launchProgress < 0.1 ? launchProgress * 10 : 1.0;
        const textAlpha = launchProgress < 0.3 ? 255 : 255 * (1 - (launchProgress - 0.3) / 0.2);
        p.fill(color[0], color[1], color[2], textAlpha);
        p.textSize(42 * textScale);
        p.textAlign(p.CENTER, p.CENTER);
        p.text('LAUNCH!', centerX, centerY - 100);
    }

    // 機体が上に飛んでいく
    const shipY = centerY - easeInCubic(launchProgress) * (p.height + 100);
    const shipScale = 1.3 + launchProgress * 0.3;

    p.push();
    p.translate(centerX, shipY);
    p.scale(shipScale);

    // ブースター炎の強度
    const flameIntensity = p.min(1, launchProgress * 3);

    // 1. 派手なブースター炎（背景層：大きく広がる炎）
    drawBoosterFlame(flameIntensity * 0.8, color);

    // 2. 機体別エンジン炎（前景層：ステージ中と同じスタイル）
    drawShipEngineFlames(shipType, flameIntensity, 45);

    // 機体
    drawShip(45);
    p.pop();
}

// ============================================
// ユーティリティ関数
// ============================================

/**
 * 現在のフェーズを判定
 */
export function getCurrentPhase(timer: number): 1 | 2 | 3 {
    if (timer <= LAUNCH_PHASES.PHASE1_END) {
        return 1;
    }
    if (timer <= LAUNCH_PHASES.PHASE2_END) {
        return 2;
    }
    return 3;
}

/**
 * フェーズ内の進行度を計算
 */
export function getPhaseProgress(timer: number, phase: 1 | 2 | 3): number {
    switch (phase) {
        case 1:
            return timer / LAUNCH_PHASES.PHASE1_END;
        case 2:
            return (timer - LAUNCH_PHASES.PHASE1_END) / (LAUNCH_PHASES.PHASE2_END - LAUNCH_PHASES.PHASE1_END);
        case 3:
            return (timer - LAUNCH_PHASES.PHASE2_END) / (LAUNCH_PHASES.TOTAL_DURATION - LAUNCH_PHASES.PHASE2_END);
    }
}

/**
 * 出撃完了判定
 */
export function isLaunchComplete(timer: number): boolean {
    return timer >= LAUNCH_PHASES.TOTAL_DURATION;
}


// ============================================
// イージング関数
// ============================================

function easeInOutCubic(t: number): number {
    const p = getP5();
    return t < 0.5 ? 4 * t * t * t : 1 - p.pow(-2 * t + 2, 3) / 2;
}

function easeInCubic(t: number): number {
    return t * t * t;
}